import React, { useEffect, useRef } from 'react';
import { Megaphone } from 'lucide-react';
import { useAdsConfig } from '../context/AdsContext';

/**
 * AdSlot — Google AdSense placeholder/renderer.
 *
 * Props:
 *  - slot: one of 'top' | 'sidebar' | 'inline' | 'bottom'
 *  - format: AdSense data-ad-format (default 'auto')
 *  - className: extra wrapper classes
 *  - height: placeholder height in px (used only when ad not configured)
 */
function AdSlot({ slot, format = 'auto', className = '', height = 90 }) {
  const { config, loading } = useAdsConfig();
  const insRef = useRef(null);
  const pushedRef = useRef(false);

  const slotKey = `slot_${slot}`;
  const slotId = config ? config[slotKey] : '';
  const enabled = config && config.enabled && config.publisher_id && slotId;

  useEffect(() => {
    if (!enabled || pushedRef.current) return;
    try {
      if (window.adsbygoogle && insRef.current) {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
        pushedRef.current = true;
      }
    } catch (e) {
      console.error('adsbygoogle push error:', e);
    }
  }, [enabled, slotId, config]);

  if (loading) return null;

  if (enabled) {
    return (
      <div className={`w-full flex justify-center my-8 ${className}`} data-testid={`ad-slot-${slot}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', width: '100%' }}
          data-ad-client={config.publisher_id}
          data-ad-slot={slotId}
          data-ad-format={format}
          data-full-width-responsive="true"
        ></ins>
      </div>
    );
  }

  // Placeholder shown when ads not configured — admin can preview the layout.
  return (
    <div
      className={`w-full flex flex-col items-center justify-center my-8 border-2 border-dashed border-[#D5D5D5] bg-white text-[#8A8A8A] ${className}`}
      style={{ minHeight: `${height}px` }}
      data-testid={`ad-slot-placeholder-${slot}`}
    >
      <div className="flex items-center gap-2">
        <Megaphone size={16} />
        <span className="text-xs tracking-[0.2em] uppercase font-bold">Advertisement</span>
      </div>
      <span className="text-[10px] tracking-[0.2em] uppercase text-[#B5B5B5] mt-1">
        Google Ads · {slot}
      </span>
    </div>
  );
}

export default AdSlot;
